import { shClient } from '$lib/shopify.svelte';
import { searchProductsQuery } from '$lib/graphql/queries';
import type {
  SearchProductsQuery,
  SearchProductsQueryVariables
} from '$lib/types/storefront.generated';
import type {
  GraphQLError,
  ShopifyResponse
} from '$lib/types/shopify';
import type { ProductNode } from '$lib/utils/shopify';

export interface SearchProductsInput {
  search: string;
  first?: number;
  after?: string | null;
  /** When provided we keep results inside that collection only. */
  productType?: string;      // e.g. "SHOES"
}


// export type CollectionData = NonNullable<GetCollectionProductsQuery['collection']>;
// export type ProductNode = CollectionData['products']['edges'][0]['node'];
// export type ProductVariantNode = ProductNode['variants']['edges'][0]['node'];
export type PageInfo = NonNullable<ShopifyResponse<SearchProductsQuery>['data']>['products']['pageInfo'];

export interface SearchProductsResult {
  products: ProductNode[];
  pageInfo: PageInfo;
}

/* Escape `:`\, `(` … so the search syntax stays valid. */
const SPECIAL = /([:\\()])/g;
const escapeQ  = (s: string) => s.replace(SPECIAL, '\\$1');

/** Build a Shopify search-syntax string (`collection_id` + fuzzy title). */
function buildSearchQuery(term: string, productType?: string): string {
  const words = term.trim()
    .split(/\s+/)
    .map(w => `title:${escapeQ(w)}*`)   // simple fuzzy prefix match per word
    .join(' AND ');

  if (!productType) return words;


  return `product_type:${productType} AND (${words})`;  //  ⬅ see docs :contentReference[oaicite:0]{index=0}
}

/** Unified, type-safe fetcher. */
export async function searchProducts(
  input: SearchProductsInput
): Promise<SearchProductsResult> {

  const { search, first = 10, after = null, productType } = input;
  if (!search.trim()) return { products: [], pageInfo: { hasNextPage:false, endCursor:null } };

  const variables: SearchProductsQueryVariables = {
    first,
    after,
    query: buildSearchQuery(search, productType),
  };
    console.debug('searchProducts variables:', variables);

  const res = await shClient.request(
    searchProductsQuery,
    { variables }
  ) as ShopifyResponse<SearchProductsQuery>;

  if (res.errors) {
    const err = new Error('Shopify GraphQL Error');
    (err as any).graphQLErrors = res.errors as GraphQLError[];
    throw err;
  }

  const edges     = res.data!.products.edges;
  const products  = edges.map(e => e.node as ProductNode);
  const pageInfo  = res.data!.products.pageInfo;

  return { products, pageInfo };
}
